/**
 * Leads date filter control — presets + custom range, sticky via parent state.
 */

import React, { useState, useEffect } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
} from "@mui/material";
import { format } from "date-fns";
import {
  LEADS_DATE_PRESETS,
  LEADS_DATE_PRESET_OPTIONS,
  formatLeadsDateFilterLabel,
  normalizeLeadsDateFilter,
  parseLeadsCalendarDate,
} from "../utils/leadsPageFilters.js";

const toInputDate = (value) => {
  if (!value) return "";
  const date = parseLeadsCalendarDate(value);
  if (!date) return "";
  return format(date, "yyyy-MM-dd");
};

const LeadsDateFilter = ({ value, onChange, minWidth = 200 }) => {
  const dateFilter = normalizeLeadsDateFilter(value);
  const [customOpen, setCustomOpen] = useState(false);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  useEffect(() => {
    if (customOpen) {
      setCustomStart(toInputDate(dateFilter.startDate));
      setCustomEnd(toInputDate(dateFilter.endDate));
    }
  }, [customOpen, dateFilter.startDate, dateFilter.endDate]);

  const selectValue =
    dateFilter.preset === LEADS_DATE_PRESETS.CUSTOM
      ? LEADS_DATE_PRESETS.CUSTOM
      : dateFilter.preset;

  const handlePresetChange = (event) => {
    const nextPreset = event.target.value;
    if (nextPreset === LEADS_DATE_PRESETS.CUSTOM) {
      setCustomOpen(true);
      return;
    }
    onChange?.({
      preset: nextPreset,
      startDate: null,
      endDate: null,
    });
  };

  const handleApplyCustom = () => {
    if (!customStart && !customEnd) return;
    let startDate = customStart || null;
    let endDate = customEnd || null;
    if (startDate && endDate && startDate > endDate) {
      [startDate, endDate] = [endDate, startDate];
    }
    onChange?.({
      preset: LEADS_DATE_PRESETS.CUSTOM,
      startDate,
      endDate,
    });
    setCustomOpen(false);
  };

  const handleCancelCustom = () => {
    setCustomOpen(false);
  };

  const label =
    dateFilter.preset === LEADS_DATE_PRESETS.CUSTOM
      ? formatLeadsDateFilterLabel(dateFilter)
      : "Filter by date";

  return (
    <>
      <FormControl sx={{ minWidth }}>
        <InputLabel id="leads-date-filter-label">{label}</InputLabel>
        <Select
          labelId="leads-date-filter-label"
          value={selectValue}
          label={label}
          onChange={handlePresetChange}
          renderValue={() => formatLeadsDateFilterLabel(dateFilter)}
        >
          {LEADS_DATE_PRESET_OPTIONS.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              onClick={() => {
                // Re-selecting Custom while already on Custom does not fire Select onChange.
                if (option.value === LEADS_DATE_PRESETS.CUSTOM) {
                  setCustomOpen(true);
                }
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Dialog open={customOpen} onClose={handleCancelCustom} maxWidth="xs" fullWidth>
        <DialogTitle>Custom date range</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Start date"
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="End date"
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelCustom}>Cancel</Button>
          <Button
            onClick={handleApplyCustom}
            variant="contained"
            disabled={!customStart && !customEnd}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LeadsDateFilter;
